import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Building2, Plus, Pencil, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { centerAPI, getErrorMessage } from '../services/api';
import { useCenters } from '../hooks/useCenters';

const emptyForm = { name: '', code: '', description: '', is_active: true };

export default function CenterList() {
  const { centers, loading, error, refresh } = useCenters();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [usersByCenter, setUsersByCenter] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(null);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error('Center name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await centerAPI.updateCenter(editingId, {
          name: form.name.trim(),
          code: form.code.trim() || null,
          description: form.description.trim() || null,
          is_active: form.is_active,
        });
        toast.success('Center updated');
      } else {
        await centerAPI.createCenter({
          name: form.name.trim(),
          code: form.code.trim() || null,
          description: form.description.trim() || null,
          is_active: form.is_active,
        });
        toast.success('Center created');
      }
      resetForm();
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (center) => {
    setEditingId(center.id);
    setForm({
      name: center.name,
      code: center.code || '',
      description: center.description || '',
      is_active: center.is_active,
    });
  };

  const toggleActive = async (center) => {
    try {
      await centerAPI.updateCenter(center.id, { is_active: !center.is_active });
      toast.success(center.is_active ? 'Center deactivated' : 'Center activated');
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const loadUsers = async (centerId) => {
    if (expandedId === centerId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(centerId);
    if (usersByCenter[centerId]) {
      return;
    }
    setLoadingUsers(centerId);
    try {
      const response = await centerAPI.getCenterUsers(centerId);
      setUsersByCenter((prev) => ({ ...prev, [centerId]: response.data.items }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingUsers(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Learning Centers</h1>
        <p className="text-gray-600 mt-1">Manage centers parents select during registration</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {editingId ? 'Edit Center' : 'Create Center'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Center name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
          <input
            type="text"
            placeholder="Code (optional)"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-2"
          />
        </div>
        <textarea
          placeholder="Description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          rows={2}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
          />
          Active (visible to parents during registration)
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {editingId ? 'Save Changes' : 'Create Center'}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>
      ) : (
        <div className="space-y-3">
          {centers.map((center) => (
            <div key={center.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">{center.name}</h3>
                    {center.code && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{center.code}</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${center.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {center.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {center.description && (
                    <p className="text-sm text-gray-600 mt-1">{center.description}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    {center.user_count} registered parent(s) · {center.payment_count} payment(s)
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => startEdit(center)}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:bg-blue-50 px-2 py-1 rounded"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => toggleActive(center)}
                    className="text-sm text-gray-600 hover:bg-gray-50 px-2 py-1 rounded"
                  >
                    {center.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => loadUsers(center.id)}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 hover:bg-gray-50 px-2 py-1 rounded"
                  >
                    <Users className="w-4 h-4" />
                    Users
                    {expandedId === center.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                </div>
              </div>

              {expandedId === center.id && (
                <div className="mt-4 border-t border-gray-100 pt-3">
                  {loadingUsers === center.id ? (
                    <p className="text-sm text-gray-500">Loading users...</p>
                  ) : (usersByCenter[center.id] || []).length > 0 ? (
                    <ul className="space-y-2">
                      {(usersByCenter[center.id] || []).map((user) => (
                        <li key={user.id} className="text-sm text-gray-700 flex justify-between">
                          <span>{user.full_name}</span>
                          <span className="text-gray-500 capitalize">{user.registration_status.replace('_', ' ')}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No users registered under this center yet.</p>
                  )}
                </div>
              )}
            </div>
          ))}
          {centers.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200 text-gray-500">
              No centers yet. Create one above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
